import React from 'react';
import { X, Sparkles, Calendar, Users, Lightbulb, Target, TrendingUp, BarChart2, Link, MapPin, Award, UserPlus } from 'lucide-react';
interface AISuggestionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  widgetType: string | null;
}
const AISuggestionsDrawer: React.FC<AISuggestionsDrawerProps> = ({
  isOpen,
  onClose,
  widgetType
}) => {
  // Get title and description based on widget type
  const getWidgetInfo = () => {
    switch (widgetType) {
      case 'impact':
        return {
          title: 'Impact Index Insights',
          description: 'AI-powered insights based on your community impact data.'
        };
      case 'connections':
        return {
          title: 'Connection Trends Insights',
          description: 'AI-powered insights based on your connection activity data.'
        };
      case 'topics':
        return {
          title: 'Active Users Insights',
          description: 'AI-powered insights based on your most active users data.'
        };
      case 'collaborations':
        return {
          title: 'Collaboration Insights',
          description: 'AI-powered insights based on your collaboration activity data.'
        };
      case 'locations':
        return {
          title: 'Location Insights',
          description: 'AI-powered insights based on your location performance data.'
        };
      default:
        return {
          title: 'AI Insights',
          description: 'AI-powered insights based on your community data.'
        };
    }
  };
  // Get suggestions based on widget type
  const getSuggestions = () => {
    switch (widgetType) {
      case 'impact':
        return [{
          id: 1,
          title: 'Technology Topic Growth Opportunity',
          description: 'Technology has a high impact index (57.8) but fewer active users than Design. This represents a growth opportunity for expanding your community.',
          icon: Target,
          category: 'Growth'
        }, {
          id: 2,
          title: 'High-Impact User Engagement',
          description: 'Users with impact scores in the 70-80 range are your most valuable community members. Consider creating an ambassador program to leverage their influence.',
          icon: Users,
          category: 'Strategy'
        }, {
          id: 3,
          title: 'Impact Variance Across Locations',
          description: 'There is a 23% variance in impact scores between your top and bottom locations. Implement successful strategies from Amsterdam in lower-performing cities.',
          icon: MapPin,
          category: 'Optimization'
        }];
      case 'connections':
        return [{
          id: 1,
          title: 'Mid-Week Engagement Peak',
          description: 'Connections peak on Wednesdays. Schedule important announcements and events mid-week to maximize engagement and participation.',
          icon: Lightbulb,
          category: 'Timing'
        }, {
          id: 2,
          title: 'Connection Growth Trend',
          description: 'New connections have increased by 15% in the last month, while active connections have plateaued. Focus on activating existing connections.',
          icon: TrendingUp,
          category: 'Trend'
        }, {
          id: 3,
          title: 'Connection Retention Strategy',
          description: 'Only 68% of new connections remain active after 4 weeks. Implement a structured onboarding process to improve retention rates.',
          icon: Users,
          category: 'Retention'
        }];
      case 'topics':
        return [{
          id: 1,
          title: 'Gold Badge Ambassador Program',
          description: 'Your Gold badge users (like Emma Schmidt and Michael Weber) have 40% more connections than average. Create an ambassador program to leverage their network and influence.',
          icon: Award,
          category: 'Engagement'
        }, {
          id: 2,
          title: 'Amsterdam-Berlin Knowledge Exchange',
          description: 'Users from Amsterdam and Berlin show the highest activity scores. Organize a virtual knowledge exchange between these two locations to share best practices.',
          icon: MapPin,
          category: 'Collaboration'
        }, {
          id: 3,
          title: 'Re-engage Inactive High-Value Users',
          description: 'Several users with high connection counts (100+) have been inactive for over 3 days. Create a targeted re-engagement campaign for these valuable community members.',
          icon: UserPlus,
          category: 'Retention'
        }];
      case 'collaborations':
        return [{
          id: 1,
          title: 'Completion Rate Improvement',
          description: 'Only 62% of started collaborations reach completion. Implement structured project management templates to improve success rates.',
          icon: Target,
          category: 'Process'
        }, {
          id: 2,
          title: 'Seasonal Collaboration Patterns',
          description: 'Collaboration starts peak in January and September. Plan major initiatives around these natural engagement cycles.',
          icon: Calendar,
          category: 'Planning'
        }, {
          id: 3,
          title: 'High-Value Collaboration Types',
          description: 'Project-based collaborations have a 2.3x higher completion rate than event-based ones. Focus resources on structured project opportunities.',
          icon: Lightbulb,
          category: 'Strategy'
        }];
      case 'locations':
        return [{
          id: 1,
          title: 'Connect Amsterdam & Berlin Communities',
          description: 'Amsterdam and Berlin show similar impact patterns but limited cross-location collaborations. Facilitating connections between these hubs could lead to innovative projects.',
          icon: Users,
          category: 'Connection'
        }, {
          id: 2,
          title: 'Barcelona Growth Opportunity',
          description: "Barcelona has the highest connections-to-users ratio but fewer total users. This indicates a highly engaged community that's ready for expansion.",
          icon: Target,
          category: 'Growth'
        }, {
          id: 3,
          title: 'Vienna Engagement Challenge',
          description: 'Vienna has many active users but fewer collaborations per user than other locations. Focus on creating more collaboration opportunities in this location.',
          icon: Link,
          category: 'Engagement'
        }];
      default:
        return [{
          id: 1,
          title: 'Organize a Design Workshop',
          description: 'The main connections happen on the Design topic. Consider organizing a workshop to strengthen these relationships and attract new community members.',
          icon: Calendar,
          category: 'Event'
        }, {
          id: 2,
          title: 'Connect Amsterdam & Berlin Communities',
          description: 'Amsterdam and Berlin show similar impact patterns but limited cross-location collaborations. Facilitating connections between these hubs could lead to innovative projects.',
          icon: Users,
          category: 'Connection'
        }, {
          id: 3,
          title: 'Technology Topic Growth Opportunity',
          description: 'Technology has a high impact index (57.8) but fewer active users than Design. This represents a growth opportunity for expanding your community.',
          icon: Target,
          category: 'Growth'
        }, {
          id: 4,
          title: 'Mid-Week Engagement Peak',
          description: 'Connections peak on Wednesdays. Schedule important announcements and events mid-week to maximize engagement and participation.',
          icon: Lightbulb,
          category: 'Insight'
        }];
    }
  };
  const widgetInfo = getWidgetInfo();
  const suggestions = getSuggestions();
  return <>
      {/* Backdrop with blur effect */}
      {isOpen && <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-177"></div>}
      {/* Drawer */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-96 md:w-[450px] bg-white shadow-xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${isOpen ? 'transform-none' : 'translate-x-full'}`} data-id="element-178">
        <div className="p-6" data-id="element-179">
          <div className="flex items-center justify-between mb-4" data-id="element-180">
            <div data-id="element-181">
              {/* AI Pill */}
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-greenback/10 rounded-full mb-2 inline-flex" data-id="element-182">
                <Sparkles className="h-3 w-3 text-greenback" data-id="element-183" />
                <span className="text-xs font-semibold text-greenback" data-id="element-184">
                  Suggerimenti AI
                </span>
              </div>
              <h2 className="text-xl font-semibold" data-id="element-185">{widgetInfo.title}</h2>
            </div>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100 transition-colors" data-id="element-186">
              <X className="h-5 w-5 text-gray-500" data-id="element-187" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6" data-id="element-188">{widgetInfo.description}</p>
          <div className="space-y-4" data-id="element-189">
            {suggestions.map(suggestion => <div key={suggestion.id} className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-190">
                <div className="flex items-start" data-id="element-191">
                  <div className="p-2 bg-greenback/10 rounded-lg mr-3" data-id="element-192">
                    <suggestion.icon className="h-5 w-5 text-greenback" data-id="element-193" />
                  </div>
                  <div data-id="element-194">
                    <div className="flex items-center mb-1" data-id="element-195">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full mr-2 bg-greenback/10 text-greenback" data-id="element-196">
                        {suggestion.category}
                      </span>
                      <h3 className="text-sm font-medium" data-id="element-197">
                        {suggestion.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-198">
                      {suggestion.description}
                    </p>
                  </div>
                </div>
                <div className="mt-3 ml-10" data-id="element-199">
                  <div style={{
                background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
                padding: '2px',
                borderRadius: '100px'
              }} data-id="element-200">
                    <button className="text-xs px-3 py-1 bg-white text-black transition-colors hover:bg-gray-50 w-full" style={{
                  borderRadius: '100px'
                }} data-id="element-201">
                      Applica Suggerimento
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </>;
};
export default AISuggestionsDrawer;